<template>
    <div :class="[outerClass, colSize, { 'form-field-disabled': isFormDisabled }]">
        <label v-if="label"
               class="block"
               :class="{ 'required-label': hasRequiredAttr, [labelClass]: true }"
               :style="labelStyle">{{
                __(label)
            }}</label>
        <Select optionLabel="label"
                optionValue="value"
                ref="dropdownRef"
                :filter="filtering"
                class="w-full"
                :scrollHeight="scrollHeight"
                :class="{ 'required-field': hasRequiredAttr, [inputClass]: true }"
                :style="inputStyle"
                :showClear="showClear"
                :validation="validation"
                :virtualScrollerOptions="itemSize ? { itemSize: itemSize } : undefined"
                :options="options"
                :disabled="isFormDisabled"
                :allowZero="allowZero"
                @change="change"
                @filter="filter"
                v-model="field"
                :pt="selectPt">
            <template #option="slotProps">
                <span v-html="slotProps.option?.[props.labelInDropdown]"></span>
            </template>
            <template #value="slotProps">
                <span v-if="slotProps.value">
                    <span v-html="options.find(opt => opt.value === slotProps.value)?.[props.labelInInput]"></span>
                </span>
                <span v-else
                      class="text-gray-400"
                      style="font-weight:normal;">{{ placeholder }}</span>
            </template>
        </Select>

    </div>
</template>

<script setup>
import { ref, onMounted, nextTick, watch } from 'vue';
import Select from 'primevue/select';
import { __, translatePrimeVueMsgs } from '@pages/Helper/utils.js'

translatePrimeVueMsgs()

const props = defineProps({
    fieldName: {
        type: String,
        required: true,
    },
    validation: {
        type: [Array, String],
        default: null,
    },
    label: {
        type: String,
        default: null,
    },
    size: {
        type: String,
        default: null
    },
    isFocus: {
        type: Boolean,
        default: false
    },
    isFormDisabled: {
        type: Boolean,
        default: false
    },
    options: {
        type: Array,
        default: [],
        required: true
    },
    change: {
        type: Function,
        required: false
    },
    filter: {
        type: Function,
        required: false,
    },
    filtering: {
        type: Boolean,
        default: true
    },
    showClear: {
        type: Boolean,
        default: false
    },
    scrollHeight: {
        type: String,
        default: '200px'
    },
    setFirst: {
        type: Boolean,
        default: false
    },
    labelClass: {
        type: String,
        default: null
    },
    labelStyle: {
        type: [Object, String],
        default: null
    },
    optionClass: {
        type: String,
        default: null
    },
    optionStyle: {
        type: [Object, String],
        default: null
    },
    inputClass: {
        type: [Object, String],
        default: null
    },
    inputStyle: {
        type: [Object, String],
        default: null
    },

    inRepeater: {
        type: Boolean,
        default: false
    },
    itemSize: {
        type: Number,
        default: null
    },
    allowZero: {
        type: Boolean,
        default: false
    },
    default: {
        type: [String, Number],
        default: null
    },
    labelInDropdown: {
        type: String,
        default: 'label'
    },
    labelInInput: {
        type: String,
        default: 'label'
    },
    placeholder: {
        type: String,
        default: ''
    }
});

const field = defineModel('field');
const validation = ref(Array.isArray(props.validation) ? props.validation : [props.validation]);
const colSize = props?.size ? 'col-' + props.size : 'col';
const hasRequiredAttr = validation.value.includes('required');
const outerClass = ref('field');

const selectPt = {
    root: ({ state }) => ({
        name: props.fieldName,
        class: [
            'w-full min-h-[2.5rem] flex items-center justify-between gap-2 rounded-md border bg-white px-3 py-2 text-sm shadow-sm transition',
            'border-gray-300 text-gray-900',
            'focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-200',
            'disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed',
            state?.overlayVisible ? 'ring-2 ring-blue-200 border-blue-500' : ''
        ]
    }),
    label: {
        style: props.inputStyle ?? props.optionStyle,
        class: [
            'flex-1 min-w-0 truncate',
            props.inputClass ?? props.optionClass
        ]
    },
    clearIcon: { class: 'text-gray-400 hover:text-gray-600 cursor-pointer' },
    dropdown: { class: 'ml-auto inline-flex items-center justify-center text-gray-500' },
    dropdownIcon: { class: 'text-gray-500' },
    overlay: { class: 'mt-1 rounded-md border border-gray-200 bg-white shadow-lg' },
    header: { class: 'px-3 py-2 border-b border-gray-100 bg-white' },
    pcFilterContainer: {
        root: { class: 'relative' }
    },
    pcFilter: {
        root: {
            class: 'w-full rounded-md border border-gray-200 bg-white pl-3 pr-10 py-2 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200'
        }
    },
    pcFilterIconContainer: {
        root: { class: 'absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none' }
    },
    filterIcon: { class: 'w-4 h-4' },
    listContainer: { class: 'overflow-auto' },
    list: { class: 'py-1' },
    option: ({ context }) => ({
        style: props.optionStyle,
        class: [
            'px-3 py-2 text-sm cursor-pointer select-none flex items-center gap-2',
            context?.disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50',
            context?.focused ? 'bg-gray-50' : '',
            context?.selected ? 'bg-primary-50 text-blue-700' : 'text-gray-700',
            props.optionClass
        ]
    }),
    optionLabel: { class: 'flex-1' },
    optionCheckIcon: { class: 'w-4 h-4 text-blue-600' },
    optionBlankIcon: { class: 'w-4 h-4 text-transparent' },
    emptyMessage: { class: 'px-3 py-2 text-sm text-gray-500' }
}

if (props.inRepeater) {
    outerClass.value = null;
}

const dropdownRef = ref(null);

// ------------------------------------------------------------------------------

watch(() => ({ field: field.value, options: props.options, default: props.default }), async (newValue, oldValue) => {
    await nextTick();

    if (field.value === 0 && !props.allowZero) {
        field.value = null;
    }

    if (props.setFirst && (field.value === null || field.value === undefined) && newValue?.options?.length > 0) {
        field.value = newValue.options[0].value;
    }
    if (props.default && (field.value === null || field.value === undefined)) {
        field.value = props.default;
    }

    // Dopasowanie wartości field do jednej z wartości w options niezależnie od typu danych
    const matchedOption = newValue.options.find(option => String(option.value) === String(field.value));
    if (matchedOption) {
        field.value = matchedOption.value;
    }
}, { immediate: true });

// ------------------------------------------------------------------------------

onMounted(async () => {
    if (props.isFocus) {
        await nextTick()
        dropdownRef.value.$el.children[0]?.focus();
    }
});

// ------------------------------------------------------------------------------

</script>

<style lang="scss" scoped>
.form-field-disabled {
    :deep([data-p="disabled"]) {
        background-color: var(--p-select-disabled-background, #f5f6f8) !important;

        // Wszystkie dzieci również
        &>* {
            background-color: var(--p-select-disabled-background, #f5f6f8) !important;
        }
    }
}
</style>
